/**
 * viewportControlFilter — shared "exclude the paper-space control viewport"
 * rule for a layout space's active Viewport entities.
 *
 * AutoCAD's PAPER SPACE sheet itself is internally represented as VIEWPORT
 * #1 (DXF group 69 `viewportId=1`) — the container the user's real
 * model-window viewports sit inside. It is never a valid MVIEW/VPCLIP/
 * pick target (mirrors `applyViewportClipSet`'s own
 * `vp.viewportId === 1` guard in `packages/command-engine/src/viewportCommands.ts`).
 *
 * Single source of this exclusion rule — `Scene3DStage`'s
 * `activeLayoutViewports` (render-visible model-window list, iter-7/iter-8
 * fix history) and `vpclipRegistry`'s `findViewportAtPaperPoint`
 * (VPCLIP-INTERACTIVE "Select viewport to clip:" hit-test, @codex round-1
 * P1) both call this so the control viewport is identified IDENTICALLY
 * everywhere (CLAUDE.md PRIORITY #2 — one truth).
 *
 * Explicit-tag-only — NO area-based inference (@codex round-10→11 P2
 * history for future readers): an earlier version of this function fell
 * back to "the largest viewport among 2+ untagged ones must be the control
 * VP" when no explicit `viewportId` was present, on the theory that this
 * editor's own DXF writer doesn't emit group 69. That theory was WRONG —
 * `dxf-io`'s parser reads group 69 into `viewportId` on import
 * (`entityParser.ts`) and the writer faithfully round-trips it back out
 * whenever the internal model has it set (`dxfBridge.entityConversion.ts`
 * / `dxfBridge.drawingToDxf.ts`), so a genuinely AutoCAD/ezdxf-authored
 * control viewport keeps its tag through this editor end-to-end and is
 * always caught by the PRIMARY branch below. The ONLY documents that ever
 * reach the fallback are ones where NO viewport carries ANY `viewportId`
 * at all — overwhelmingly layouts created (in full or in part) entirely
 * within this app, where `shell/add-space` seeds a brand-new layout with
 * `entities: []` and MVIEW/create-viewport never assigns a `viewportId`
 * (see `geometryReducerLayout.ts` / `geometryMutations.ts`). In that case
 * there is no control-viewport entity hiding among the set at all — EVERY
 * active viewport is a real, user-created model window, and "largest area"
 * is pure incidental geometry, not evidence of anything (violates this
 * project's own "never derive a semantic field from incidental presence"
 * rule — see memory `feedback-roundtrip-preserve-source-never-infer`).
 * A sheet-size-aware 70%-of-sheet-area threshold (round-10 P2) narrowed
 * but could not close this: a real MVIEW deliberately drafted to fill
 * (nearly) the whole printable sheet is legitimately common and is
 * geometrically indistinguishable from a true control viewport by area
 * alone (GitHub Codex round-10 P2, "full-sheet MVIEWs" finding) — no
 * threshold fixes that, because the ambiguity is fundamental, not a
 * calibration problem. Round-11 removes the heuristic outright: without
 * an explicit tag, nothing is excluded.
 *
 * A follow-up GitHub Codex review of round-11 raised a theoretical
 * counter-concern: documents exported by an OLDER version of this editor
 * might have written control-viewport geometry WITHOUT the group-69 tag,
 * leaving such "legacy" files permanently unable to identify their control
 * viewport at all (it would render as a fake model window AND become
 * VPCLIP-mutable, since `applyViewportClipSet`'s own `viewportId===1`
 * guard is equally blind to it). Investigated and NOT reinstated: this is
 * not reproducible against current HEAD — `dxfBridge.viewport.test.ts`'s
 * "the paper-space CONTROL viewport's group-69 viewportId=1 survives
 * export -> re-import" test empirically proves the CURRENT bridge
 * round-trips `viewportId` losslessly (import → export → re-import keeps
 * `viewportId=1` on the same entity), so a document imported from genuine
 * AutoCAD/ezdxf-authored DXF and re-saved by this editor never loses the
 * tag going forward. No evidence (test fixture, changelog, or git history)
 * of a PAST writer version that dropped the tag was found either. Per
 * CLAUDE.md's allowance for documenting a known-but-unconfirmed gap with a
 * plan rather than guessing: IF a genuinely tag-losing legacy file is ever
 * found in the wild, the correct fix is a targeted, import-time-scoped
 * repair/migration pass (which could safely use geometry — it would only
 * ever run against documents already known to be suspect) — not a
 * permanent geometry-based guess baked into this live runtime exclusion
 * rule, which round-10 already proved regresses the common, legitimate
 * full-sheet-MVIEW layout style for every user, every session.
 */
export function excludeControlViewport<T extends { viewportId?: number }>(
  viewports: ReadonlyArray<T>,
): T[] {
  // The control viewport (paper space itself) is ONLY ever identifiable by
  // its explicit DXF group-69 tag — see the doc comment above for why an
  // area-based fallback was tried and removed. No tag, no exclusion.
  if (viewports.some((vp) => (vp.viewportId ?? 0) === 1)) {
    return viewports.filter((vp) => (vp.viewportId ?? 0) !== 1);
  }
  return [...viewports];
}
